import * as React from "react"
import AppLayout from "@/components/AppLayout"
import PageTitle from "@/components/PageTitle"
import { Button } from "@/components/ui/Button"
import { Badge } from "@/components/ui/Badge"
import { Card } from "@/components/ui/Card"
import { Input } from "@/components/ui/Input"
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/Table"
import CustomerFormDialog, {
  type Customer,
  type Member,
  TYPE_LABELS,
} from "./components/CustomerFormDialog"

type Props = {
  customers: Customer[]
  members: Member[]
  can: { manage: boolean; edit_external_id: boolean }
}

export default function WholesaleCustomersIndex({ customers, members, can }: Props) {
  const [query, setQuery] = React.useState("")
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<Customer | null>(null)

  const visible = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return customers
    return customers.filter((c) => c.name.toLowerCase().includes(q))
  }, [customers, query])

  const openCreate = () => {
    setEditing(null)
    setDialogOpen(true)
  }

  const openEdit = (customer: Customer) => {
    setEditing(customer)
    setDialogOpen(true)
  }

  return (
    <AppLayout
      header={
        <PageTitle
          title="Wholesale Customers"
          subtitle="Distributor directory for the wholesale channel"
        />
      }
      headerActions={can.manage && <Button onClick={openCreate}>New customer</Button>}
    >
      <div className="flex flex-col gap-4">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name…"
          className="max-w-xs"
        />

        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>External ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>PIC</TableHead>
                <TableHead>Province</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((customer) => (
                <TableRow key={customer.id}>
                  <TableCell className="font-mono text-xs text-muted-foreground">{customer.external_id}</TableCell>
                  <TableCell className="font-medium">
                    {can.manage ? (
                      <Button
                        variant="link"
                        className="h-auto p-0 font-medium"
                        onClick={() => openEdit(customer)}
                      >
                        {customer.name}
                      </Button>
                    ) : (
                      customer.name
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{TYPE_LABELS[customer.customer_type]}</Badge>
                  </TableCell>
                  <TableCell>{customer.pic_user?.name ?? "—"}</TableCell>
                  <TableCell>{customer.province ?? "—"}</TableCell>
                  <TableCell className="max-w-md truncate text-muted-foreground" title={customer.notes ?? undefined}>
                    {customer.notes ?? ""}
                  </TableCell>
                </TableRow>
              ))}
              {visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    {customers.length === 0 ? "No customers yet." : "No customers match your search."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      <CustomerFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        customer={editing}
        members={members}
        canEditExternalId={can.edit_external_id}
      />
    </AppLayout>
  )
}
